import { supabase } from "@/integrations/supabase/client";

export type ConversationPeer = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  slug: string | null;
  isAmbassador: boolean;
};

export type ConversationSummary = {
  id: string;
  kind: string;
  subject: string | null;
  contextUrl: string | null;
  lastMessageAt: string;
  lastMessagePreview: string | null;
  lastSenderId: string | null;
  unread: number;
  muted: boolean;
  peers: ConversationPeer[];
};

type ParticipantRow = {
  conversation_id: string;
  user_id: string;
  last_read_at: string;
  muted: boolean;
};

export async function listMyConversations(userId: string): Promise<ConversationSummary[]> {
  const { data: mine, error: mineError } = await supabase
    .from("conversation_participants")
    .select("conversation_id,last_read_at,muted")
    .eq("user_id", userId)
    .eq("archived", false);
  if (mineError) throw mineError;

  const ids = (mine ?? []).map((row) => row.conversation_id);
  if (ids.length === 0) return [];

  const [{ data: conversations, error: conversationsError }, { data: participants, error: participantsError }] =
    await Promise.all([
      supabase
        .from("conversations")
        .select("id,kind,subject,context_url,last_message_at,last_message_preview,last_sender_id")
        .in("id", ids)
        .order("last_message_at", { ascending: false }),
      supabase
        .from("conversation_participants")
        .select("conversation_id,user_id,last_read_at,muted")
        .in("conversation_id", ids),
    ]);
  if (conversationsError) throw conversationsError;
  if (participantsError) throw participantsError;

  const participantRows = (participants ?? []) as ParticipantRow[];
  const peerIds = Array.from(
    new Set(participantRows.map((row) => row.user_id).filter((id) => id !== userId)),
  );

  const profilesById = new Map<string, ConversationPeer>();
  if (peerIds.length > 0) {
    const { data: profiles, error: profilesError } = await supabase
      .from("profiles")
      .select("id,display_name,avatar_url,slug,is_ambassador")
      .in("id", peerIds);
    if (profilesError) throw profilesError;
    for (const profile of profiles ?? []) {
      profilesById.set(profile.id, {
        id: profile.id,
        displayName: profile.display_name ?? "BeHot member",
        avatarUrl: profile.avatar_url,
        slug: profile.slug,
        isAmbassador: Boolean(profile.is_ambassador),
      });
    }
  }

  const { data: unreadRows, error: unreadError } = await supabase
    .from("messages")
    .select("conversation_id,sender_id,created_at")
    .in("conversation_id", ids)
    .neq("sender_id", userId)
    .order("created_at", { ascending: false })
    .limit(1000);
  if (unreadError) throw unreadError;

  const ownMeta = new Map((mine ?? []).map((row) => [row.conversation_id, row]));
  return (conversations ?? []).map((conversation) => {
    const lastRead = ownMeta.get(conversation.id)?.last_read_at ?? "1970-01-01T00:00:00Z";
    return {
      id: conversation.id,
      kind: conversation.kind,
      subject: conversation.subject,
      contextUrl: conversation.context_url,
      lastMessageAt: conversation.last_message_at,
      lastMessagePreview: conversation.last_message_preview,
      lastSenderId: conversation.last_sender_id,
      unread: (unreadRows ?? []).filter(
        (message) => message.conversation_id === conversation.id && message.created_at > lastRead,
      ).length,
      muted: Boolean(ownMeta.get(conversation.id)?.muted),
      peers: participantRows
        .filter((row) => row.conversation_id === conversation.id && row.user_id !== userId)
        .map((row) => profilesById.get(row.user_id))
        .filter((peer): peer is ConversationPeer => Boolean(peer)),
    };
  });
}

export async function findMessageRecipient(handle: string, userId: string): Promise<ConversationPeer | null> {
  const needle = handle.replace(/^@/, "").trim();
  if (!needle) return null;
  const escaped = needle.replace(/[,%()]/g, "");
  const { data, error } = await supabase
    .from("profiles")
    .select("id,display_name,avatar_url,slug,handle,is_ambassador,is_suspended")
    .or(`handle.ilike.${escaped},slug.ilike.${escaped},display_name.ilike.${escaped}`)
    .limit(5);
  if (error) throw error;
  const match = (data ?? []).find((profile) => profile.id !== userId && !profile.is_suspended);
  if (!match) return null;
  return {
    id: match.id,
    displayName: match.display_name ?? "BeHot member",
    avatarUrl: match.avatar_url,
    slug: match.slug ?? match.handle,
    isAmbassador: Boolean(match.is_ambassador),
  };
}
